import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import { ConfirmProvider } from "@/components/ConfirmDialog";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AdminLayout from "@/layouts/AdminLayout";
import StudentLayout from "@/layouts/StudentLayout";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminStudents from "@/pages/admin/Students";
import AdminClasses from "@/pages/admin/Classes";
import AdminClassDetail from "@/pages/admin/ClassDetail";
import AdminAttendance from "@/pages/admin/Attendance";
import AdminEvaluations from "@/pages/admin/Evaluations";
import AdminAnnouncements from "@/pages/admin/Announcements";
import AdminFinance from "@/pages/admin/Finance";
import AdminStudentDetail from "@/pages/admin/StudentDetail";
import AdminClassCards from "@/pages/admin/ClassCards";
import AdminAccount from "@/pages/admin/Account";
import AdminCalendar from "@/pages/admin/Calendar";

import StudentHome from "@/pages/student/Home";
import StudentAttendance from "@/pages/student/MyAttendance";
import StudentEvaluations from "@/pages/student/MyEvaluations";
import StudentEvolution from "@/pages/student/MyEvolution";
import StudentFinance from "@/pages/student/MyFinance";
import StudentAnnouncements from "@/pages/student/Announcements";
import StudentClass from "@/pages/student/MyClass";
import StudentEditProfile from "@/pages/student/EditProfile";
import StudentCalendar from "@/pages/student/MyCalendar";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="app-loading">
        Carregando...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/aluno"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/aluno"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <Toaster richColors position="top-right" theme="dark" />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            <Route path="/admin" element={<Protected role="admin"><AdminLayout /></Protected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="alunos" element={<AdminStudents />} />
              <Route path="alunos/:id" element={<AdminStudentDetail />} />
              <Route path="turmas" element={<AdminClasses />} />
              <Route path="turmas/:id" element={<AdminClassDetail />} />
              <Route path="presenca" element={<AdminAttendance />} />
              <Route path="avaliacoes" element={<AdminEvaluations />} />
              <Route path="avisos" element={<AdminAnnouncements />} />
              <Route path="financeiro" element={<AdminFinance />} />
              <Route path="carteirinhas" element={<AdminClassCards />} />
              <Route path="calendario" element={<AdminCalendar />} />
              <Route path="conta" element={<AdminAccount />} />
            </Route>

            <Route path="/aluno" element={<Protected role="student"><StudentLayout /></Protected>}>
              <Route index element={<StudentHome />} />
              <Route path="turma" element={<StudentClass />} />
              <Route path="presenca" element={<StudentAttendance />} />
              <Route path="avaliacoes" element={<StudentEvaluations />} />
              <Route path="evolucao" element={<StudentEvolution />} />
              <Route path="financeiro" element={<StudentFinance />} />
              <Route path="avisos" element={<StudentAnnouncements />} />
              <Route path="perfil" element={<StudentEditProfile />} />
              <Route path="calendario" element={<StudentCalendar />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
